import { css } from 'styled-components';
import { Link, Placeholder, Text } from '@lmn/design-system';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { Icon } from '@/app/common/icons/Icon';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { useChangeTransportOpenModal } from '@/app/components/hotel-detail/evo/transport/EvoChangeTransportCard/ChangeTransportModalProvider';
import { useChangeTransportDetailsOpenModal } from '@/app/components/hotel-detail/changeTransport/ChangeTransportDetailsModalProvider';
import { useTrackChangeTransportLinkClicked } from '@/app/components/hotel-detail/evo/hooks/useTrackChangeTransportLinkClicked.ts';
import { SubtleButton } from '@/app/components/hotel-detail/common/button';
import { useTranslation } from '@/app/components/translations/Translation';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { FLIGHTS_DATES_FORMAT, getFlightDates } from '@/app/components/hotel-detail/common/dates';
import { getCheckedBaggageText } from '@/app/components/hotel-detail/common/luggageTranslations';
import {
  TransportLegs,
  SummaryHeader,
  SummaryRow,
  SummaryRowPlaceholder,
  SummaryRows,
  SummarySection,
} from './summarySection.styles';
import { TransportLegRow } from './TransportLegRow';

export function TransportSummarySection({ showModifyLink = true }: { showModifyLink?: boolean }) {
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { locale } = localization;
  const { offersData, isLoadingRooms } = useOffersContext();
  const { adults, children, isTrain } = useUrlSearchParams();
  const { openModal: openChangeTransportModal } = useChangeTransportOpenModal();
  const { openModal: openDetailsTransportModal } = useChangeTransportDetailsOpenModal();
  const trackChangeTransportLinkClicked = useTrackChangeTransportLinkClicked();
  const { selectedBrandedFare } = useSelectedMealPlan();

  const transport = offersData?.transport?.transports?.[0];
  const outbound = transport?.outbound;
  const inbound = transport?.inbound;

  // Curated offers include checked bags per booking (numberOfBaggagePiecesIncluded), not per passenger.
  const numberOfBaggagePiecesIncluded = transport?.numberOfBaggagePiecesIncluded ?? 0;
  const showGroupLuggage = numberOfBaggagePiecesIncluded > 0;
  // MPH-932: the summary promises what the whole trip carries, so a piece counts only when both
  // bounds include it — the outbound alone used to decide, and an inbound without a cabin bag
  // still read "2 x cabin bags".
  const checkedBagIncluded =
    !!selectedBrandedFare?.holdBaggage || (outbound?.includeBaggage && inbound?.includeBaggage) || showGroupLuggage;
  const carryOnBagIncluded =
    !!selectedBrandedFare?.cabinBaggage || (outbound?.handBaggageIncluded && inbound?.handBaggageIncluded);
  const numberOfLuggage = adults + children;

  const originTransportCity = outbound?.segments?.[0]?.departureHub.cityName;

  const flightDays =
    outbound && inbound
      ? getFlightDates(locale, outbound.departureDate, inbound.departureDate, FLIGHTS_DATES_FORMAT)
      : null;

  return (
    <SummarySection>
      <SummaryHeader>
        <Text variant="Title5" weight="bold">
          {originTransportCity
            ? t(
                isTrain
                  ? 'vacation.select.cc.trip_overview.train_from'
                  : 'vacation.select.cc.trip_overview.flight_from',
                { origin: originTransportCity }
              )
            : t(
                isTrain
                  ? 'vacation.select.cc.trip_overview.return_train'
                  : 'vacation.select.cc.trip_overview.return_flight'
              )}
        </Text>
        {showModifyLink && (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          <Link
            variant="accent"
            appearance="underlined"
            onClick={() => {
              trackChangeTransportLinkClicked();
              openChangeTransportModal();
            }}
            css={css`
              ${({ theme }) => theme.kallisto.mixins.font('Body')};
              white-space: nowrap;
            `}
          >
            {t('vacation.select.cc.trip_overview.change_flight')}
          </Link>
        )}
      </SummaryHeader>
      <SummaryRows>
        {!isLoadingRooms && outbound && inbound && flightDays ? (
          <SubtleButton onClick={() => openDetailsTransportModal(transport)} css="text-decoration: none;">
            <TransportLegs>
              <TransportLegRow leg={outbound} day={flightDays.checkin} />
              <TransportLegRow leg={inbound} day={flightDays.checkout} />
            </TransportLegs>
          </SubtleButton>
        ) : (
          <Placeholder height="16px" width="220px" />
        )}

        <SummaryRow>
          {offersData?.transport ? (
            <>
              <Icon category="Luggage" name="icon-backpack" size={20} color="iconPrimary" alt="small bag" />
              <Text variant="Body">
                {numberOfLuggage > 1
                  ? t('vacation.select.cc.luggage.hand.plural', { num: numberOfLuggage.toString() })
                  : t('vacation.select.cc.luggage.hand.single')}
              </Text>
            </>
          ) : (
            <SummaryRowPlaceholder width="160px" />
          )}
        </SummaryRow>

        {carryOnBagIncluded && (
          <SummaryRow>
            <Icon category="Luggage" name="icon-carry-on" size={20} color="iconPrimary" alt="carry on bag" />
            <Text variant="Body">
              {numberOfLuggage > 1
                ? t('vacation.select.cc.luggage.cabin.plural', { num: numberOfLuggage.toString() })
                : t('vacation.select.cc.luggage.cabin.single')}
            </Text>
          </SummaryRow>
        )}

        {checkedBagIncluded && (
          <SummaryRow>
            <Icon category="Luggage" name="icon-suitcase" size={20} color="iconPrimary" alt="checked bag" />
            <Text variant="Body">
              {getCheckedBaggageText({ showGroupLuggage, numberOfBaggagePiecesIncluded, numberOfLuggage, t })}
            </Text>
          </SummaryRow>
        )}
      </SummaryRows>
    </SummarySection>
  );
}
